export type NavDirection = 'up' | 'down' | 'left' | 'right';

/** The parts of a `DOMRect` the rules below read, so they can be exercised without a DOM. */
export interface NavRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

export interface NavPlacement {
  name: string;
  rect: NavRect;
}

/** Members of one row rarely share an exact edge, so every comparison here is made with slack. */
const TOLERANCE = 4;

const centreX = (rect: NavRect) => rect.left + rect.width / 2;
const centreY = (rect: NavRect) => rect.top + rect.height / 2;

/** Vertical spans overlap, i.e. the two sit side by side rather than one above the other. */
const sharesRow = (a: NavRect, b: NavRect) =>
  a.top < b.top + b.height - TOLERANCE && b.top < a.top + a.height - TOLERANCE;

/** Ties go to whichever registered first, so a cell centred between two others always picks the same one. */
const closestBy = (placements: NavPlacement[], distance: (rect: NavRect) => number) =>
  placements.reduce<NavPlacement | null>(
    (best, placement) => (best === null || distance(placement.rect) < distance(best.rect) ? placement : best),
    null,
  )?.name ?? null;

/**
 * The next control in a direction, read from where the controls actually are on screen rather than
 * from the order they registered in.
 *
 * A screen laid out as a grid registers in reading order, which makes "the next one in the list" and
 * "the one below" different elements - on the main menu, Down from the first cell of a two-column
 * block would otherwise land on the cell to its right. Taking the positions from the rendered boxes
 * means a layout that changes with the viewport has nothing to declare twice.
 *
 * Up and down wrap, the way the flat vertical list has always wrapped. Left and right do not: a
 * row's edges are what make a grid read as a grid rather than as one long wrapped list.
 */
export default function findNavNeighbour(
  placements: NavPlacement[],
  current: string,
  direction: NavDirection,
): string | null {
  const fromIndex = placements.findIndex((placement) => placement.name === current);
  if (fromIndex === -1) return null;

  const from = placements[fromIndex].rect;
  const candidates = placements.filter((_, index) => index !== fromIndex);
  if (!candidates.length) return null;

  if (direction === 'left' || direction === 'right') {
    const towards = direction === 'right' ? 1 : -1;
    const alongside = candidates.filter(
      (placement) => sharesRow(from, placement.rect) && towards * (centreX(placement.rect) - centreX(from)) > TOLERANCE,
    );

    return closestBy(alongside, (rect) => Math.abs(centreX(rect) - centreX(from)));
  }

  const towards = direction === 'down' ? 1 : -1;
  const gap = (placement: NavPlacement) => towards * (centreY(placement.rect) - centreY(from));
  const ahead = candidates.filter((placement) => gap(placement) > TOLERANCE);
  const behind = candidates.filter((placement) => gap(placement) < -TOLERANCE);
  // Nothing ahead means wrapping round, and the row to land on is then the furthest one back.
  const pool = ahead.length ? ahead : behind;
  if (!pool.length) return null;

  const distances = pool.map((placement) => Math.abs(gap(placement)));
  const targetRow = ahead.length ? Math.min(...distances) : Math.max(...distances);
  const row = pool.filter((placement) => Math.abs(Math.abs(gap(placement)) - targetRow) <= TOLERANCE);

  return closestBy(row, (rect) => Math.abs(centreX(rect) - centreX(from)));
}
